import { useEffect, useRef } from 'react';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Checkbox,
  Tooltip,
} from '@qodo/design-system';
import { FolderGit2 } from 'lucide-react';
import { GitlabProjectResponse } from 'network/httpClients/GitlabClient';
import { useGitlabGroupRepositoriesQuery } from 'network/queries/useGitlabRepositoriesQuery';
import { spawn } from 'utils/translations';
import { Loader } from 'components/Loader';
import { SELECTION_ACTIONS } from '../../consts';
import { GroupSectionProps } from '../../types';

export const GroupSection = ({
  group,
  appConfigId,
  searchQuery,
  isExpanded,
  selectedIds,
  selectedGroupIds,
  installedIds,
  dispatch,
  onInstalledIdsLoaded,
  hideGroupSelect = false,
  baselineSelectedCount = 0,
  depth = 0,
  isCoveredByRoot = false,
  coveredByName,
}: GroupSectionProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const spawnStep = spawn('dialogs.gitlab-installation.connect-repositories');

  const {
    data: repositoriesPages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useGitlabGroupRepositoriesQuery(appConfigId, group.id, searchQuery, {
    enabled: isExpanded,
  });

  const repositories = repositoriesPages?.pages.flatMap((p) => p.items) ?? [];
  const repositoriesTotal =
    repositoriesPages?.pages[0]?.total ?? group.repositoriesCount;
  const isWholeGroupSelected =
    !hideGroupSelect && selectedGroupIds.has(group.id);
  const isCovered = !hideGroupSelect && isCoveredByRoot;
  const isFullySelected = isWholeGroupSelected || isCovered;
  const groupProjectIds = repositories.map((p) => p.id);

  useEffect(() => {
    if (!repositoriesPages?.pages?.length) return;

    const lastPage =
      repositoriesPages.pages[repositoriesPages.pages.length - 1];
    const allIds = lastPage.items.map((p) => p.id);
    const newInstalledIds = lastPage.items
      .filter((p) => p.hasWebhook)
      .map((p) => p.id);

    dispatch({
      type: SELECTION_ACTIONS.REGISTER_GROUP_PROJECTS,
      groupId: group.id,
      ids: allIds,
    });
    onInstalledIdsLoaded(newInstalledIds);
  }, [repositoriesPages, group.id, onInstalledIdsLoaded, dispatch]);

  useEffect(() => {
    if (!isExpanded) return;

    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [isExpanded, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Once the group's projects have loaded, count the live selection against
  // them. Before that (collapsed, or cache evicted) fall back to the connected
  // baseline so the counter reflects reality instead of showing 0.
  const liveSelectedCount = groupProjectIds.filter((id) =>
    selectedIds.has(id),
  ).length;
  const selectedGroupCount = isFullySelected
    ? repositoriesTotal ?? 0
    : groupProjectIds.length > 0
      ? liveSelectedCount
      : baselineSelectedCount;

  const onGroupCheckboxChange = () => {
    if (!isWholeGroupSelected) {
      dispatch({
        type: SELECTION_ACTIONS.CLEAR_GROUP_PROJECTS,
        groupId: group.id,
      });
    }

    dispatch({ type: SELECTION_ACTIONS.TOGGLE_WHOLE_GROUP, id: group.id });
  };

  const onProjectCheckedChange = (projectId: number) => {
    if (isWholeGroupSelected) {
      dispatch({
        type: SELECTION_ACTIONS.DESELECT_GROUP_AND_EXCLUDE_PROJECT,
        groupId: group.id,
        excludeId: projectId,
        loadedIds: groupProjectIds,
      });
    } else {
      dispatch({ type: SELECTION_ACTIONS.TOGGLE_PROJECT, id: projectId });
    }
  };

  const renderProject = (project: GitlabProjectResponse) => {
    const isInstalled = installedIds.has(project.id);
    const isDisabled = isInstalled || isFullySelected;

    const tooltipContent = (() => {
      if (isInstalled) return spawnStep('already-installed-tooltip');

      if (isCovered)
        return spawnStep('covered-by-group-tooltip', { group: coveredByName });

      if (isWholeGroupSelected) return spawnStep('group-loading-tooltip');

      return undefined;
    })();

    return (
      <div key={project.id} className="flex items-center px-6 py-1.5">
        <Tooltip content={tooltipContent} triggerProps={{ type: 'button' }}>
          <Checkbox
            name={`gitlab-project-${project.id}`}
            checked={isFullySelected || selectedIds.has(project.id)}
            disabled={isDisabled}
            onCheckedChange={() => onProjectCheckedChange(project.id)}
            wrapperClassName="p-0 gap-3"
            aria-label={project.name}
            label={project.name}
          />
        </Tooltip>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader className="size-5" />
        </div>
      );
    }

    if (isError) {
      return (
        <p className="text-sm text-destructive px-6 py-3">
          {spawnStep('group-fetch-error')}
        </p>
      );
    }

    if (repositories.length === 0) {
      return (
        <p className="text-sm text-muted-foreground px-6 py-3">
          {spawnStep('group-no-repos')}
        </p>
      );
    }

    return (
      <>
        {repositories.map(renderProject)}
        <div ref={sentinelRef} className="shrink-0 h-1" />
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-3">
            <Loader className="size-4" />
          </div>
        )}
      </>
    );
  };

  const groupLabel = (
    <div className="flex items-center gap-3 flex-1">
      <FolderGit2 className="size-4 shrink-0 text-text-interactive-neutral-idle" />
      {group.name}
      {repositoriesTotal !== undefined && (
        <span className="text-xs text-text-panel-neutral-mid">
          {spawnStep('group-count', {
            selected: selectedGroupCount,
            total: repositoriesTotal,
          })}
        </span>
      )}
      {!hideGroupSelect && (
        <span className="text-xs text-text-panel-neutral-mid">
          {spawnStep('includes-subgroups-note')}
        </span>
      )}
    </div>
  );

  return (
    <AccordionItem
      value={group.fullPath}
      data-depth={depth}
      className="px-3 ml-4 border-l border-border-interactive-neutral-idle data-[depth=0]:ml-0 data-[depth=0]:border-l-0"
    >
      <AccordionTrigger className="flex-1 text-sm font-medium py-2">
        {hideGroupSelect ? (
          // No group-select control: let clicking the label toggle the accordion.
          groupLabel
        ) : (
          // stopPropagation only around the checkbox so toggling whole-group
          // selection doesn't also expand/collapse the accordion.
          <div className="flex gap-3" onClick={(e) => e.stopPropagation()}>
            <Tooltip
              content={
                isCovered
                  ? spawnStep('covered-by-group-tooltip', {
                      group: coveredByName,
                    })
                  : undefined
              }
              triggerProps={{ type: 'button' }}
            >
              <Checkbox
                name={`gitlab-group-${group.fullPath}`}
                checked={isFullySelected}
                disabled={isCovered}
                onCheckedChange={onGroupCheckboxChange}
                wrapperClassName="p-0 gap-3"
                aria-label={group.fullPath}
                label={groupLabel}
              />
            </Tooltip>
          </div>
        )}
      </AccordionTrigger>

      <AccordionContent className="border border-border-interactive-neutral-idle rounded-sm mb-2 p-0">
        <div
          className="flex flex-col gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {renderContent()}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
